/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getAuthData } from "../features/auth/authSlice";
import {
  getCompanyData,
  getPlansByCompanyId,
  openModal,
  removeCompanyPlan,
} from "../features/company/companySlice";
import {
  closeModal as closePaymentModal,
  getPaymentData,
  makeBulkPayment,
  makePayment,
  openModal as openPaymentModal,
} from "../features/payment/paymentSlice";
import Alert from "../utils/alert";
import config from "../utils/config";
import Menu from "../utils/data.dropdown";
import { capitalize, getAmount, getIds } from "../utils/helper";

const { routes } = config;

export default function UsePlans() {
  const navigate = useNavigate(),
    { plans, loading, pagination, modal } = useSelector(getCompanyData),
    { modal: paymentModal } = useSelector(getPaymentData),
    { user: authUser } = useSelector(getAuthData),
    [cancelPlanPayload, setCancelPlanPayload] = useState({
      name: "",
      planId: "",
    }),
    [singlePaymentData, setSinglePaymentData] = useState(null),
    [bulkPaymentData, setBulkPaymentData] = useState(null),
    dispatch = useDispatch(),
    [checkedList, setCheckedList] = useState([]);

  useEffect(() => {
    (async () =>
      await dispatch(getPlansByCompanyId({ companyId: getIds()?.company })))();
  }, [pagination.page]);

  useEffect(() => {
    if (checkedList.length > 0) {
      // return an error and remove the plan id if any checked plan is active
      checkedList.forEach((id) => {
        const plan = plans.find((plan) => plan.planId === id);
        if (plan && plan.active) {
          // remove the active plan from the checked list
          setCheckedList((prev) => prev.filter((item) => item !== id));

          Alert({
            message: `Plan "${plan?.Plan?.name}" is already active and cannot be selected for payment.`,
            type: "error",
          });
        }
      });
    }
  }, [checkedList]);

  const singlePaystackConfig = useMemo(() => {
    return {
      reference: `PAY_SINGLE_${singlePaymentData?.planId}_${Date.now()}`,
      email:
        authUser?.companies?.[0]?.emails ||
        authUser?.email ||
        "noreply@example.com",
      amount: Math.max(singlePaymentData?.amount * 100, 100), // Amount in kobo
      publicKey: config.paystackPublicKey,
    };
  }, [singlePaymentData]);

  // Handle single payment confirmation - triggers Paystack
  const handleSinglePaymentConfirm = (reference) => {
    if (!singlePaymentData) return;

    dispatch(closePaymentModal("confirmation"));

    // Create Paystack config with current payment data

    // Success handler
    const onSuccess = async (reference) => {
      const paymentPayload = {
        payeeId: singlePaymentData?.hmoId,
        payeeType: "hmo",
        payerId: getIds()?.company,
        payerType: "company",
        planId: singlePaymentData?.planId,
        purpose: `Payment for ${singlePaymentData?.planName}`,
        amount: singlePaymentData.amount.toString(),
        details: `Plan payment from ${authUser?.companies?.[0]?.name} for ${singlePaymentData?.planName}`,
        paymentReference: reference?.reference,
      };

      const res = await dispatch(makePayment(paymentPayload)).unwrap();
      if (res?.success) {
        setSinglePaymentData(null);
        setCheckedList([]);
        await dispatch(getPlansByCompanyId({ companyId: getIds().company }));
      }
    };

    onSuccess(reference);
  };

  const bulkPaystackConfig = useMemo(() => {
    // Calculate total amount
    const totalAmount = bulkPaymentData?.plans?.reduce(
      (sum, plan) => sum + parseFloat(plan?.Plan?.amount || 0),
      0
    );

    // Create Paystack config with current payment data
    const paystackConfig = {
      reference: `PAY_BULK_${bulkPaymentData?.plans?.length}_${Date.now()}`,
      email:
        authUser?.companies?.[0]?.email ||
        authUser?.email ||
        "noreply@example.com",
      amount: Math.max(totalAmount * 100, 100), // Amount in kobo
      publicKey: config.paystackPublicKey,
    };

    return paystackConfig;
  }, [bulkPaymentData]);

  // Handle bulk payment confirmation - triggers Paystack
  const handleBulkPaymentConfirm = (reference) => {
    if (!bulkPaymentData?.plans?.length) return;

    dispatch(closePaymentModal("bulkConfirmation"));

    // Success handler
    const onSuccess = async (reference) => {
      const paymentPayload = {
        plans: bulkPaymentData.plans,
        paymentReference: reference.reference,
        companyId: getIds().company,
      };

      const res = await dispatch(makeBulkPayment(paymentPayload)).unwrap();
      if (res?.success) {
        setBulkPaymentData(null);
        setCheckedList([]);
        await dispatch(getPlansByCompanyId({ companyId: getIds().company }));
      }
    };

    onSuccess(reference);
  };

  // Handle bulk payment button click
  const handleBulkPayment = () => {
    if (checkedList.length === 0) return;

    const selectedPlans = plans.filter((plan) =>
      checkedList.includes(plan.planId)
    );
    setBulkPaymentData({ plans: selectedPlans });
    dispatch(openPaymentModal("bulkConfirmation"));
  };

  const data = plans?.map((plan) => ({
    "Plan Name": plan?.Plan?.name,
    "Unique Name": plan?.Plan?.uniqueName,
    "HMO Name": capitalize(plan?.Plan?.Hmo?.name),
    "Amount Per Plan": "₦" + getAmount(plan?.Plan?.amount),
    "Plan Duration": plan?.Plan?.duration,
    "Sub. Status": plan?.active ? "Active" : "Inactive",
    _data: plan,
    _id: plan.planId,
  }));

  const menu = new Menu({});
  const dropdownMenu = [
      menu.payForPlan({
        action: (value) => {
          setSinglePaymentData({
            planId: value.planId,
            planName: value?.Plan?.name,
            hmoName: capitalize(value?.Plan?.Hmo?.name),
            hmoId: value?.Plan?.hmoId,
            amount: parseFloat(value?.Plan?.amount || 0),
          });
          dispatch(openPaymentModal("confirmation"));
        },
      }),
      menu.remove({
        type: "Plan",
        action: (value) => {
          setCancelPlanPayload({
            planId: value.planId,
            name: value?.Plan?.name,
          });
          dispatch(openModal("delete"));
        },
      }),
      menu.viewCompanyPlanDetails({
        navigate,
        routes,
      }),
    ],
    handleSuccess = async () => {
      const res = await dispatch(
        removeCompanyPlan({
          planId: cancelPlanPayload.planId,
          companyId: getIds().company,
        })
      ).unwrap();
      res?.success &&
        (async () =>
          await dispatch(
            getPlansByCompanyId({ companyId: getIds().company })
          ))();
    };

  return {
    data,
    pagination,
    loading,
    dropdownMenu,
    modal,
    checkedList,
    setCheckedList,
    handleBulkPayment,
    singlePaymentData,
    bulkPaymentData,
    singlePaystackConfig,
    bulkPaystackConfig,
    handleSinglePaymentConfirm,
    handleBulkPaymentConfirm,
    cancelPlanPayload,
    dispatch,
    plans,
    routes,
    handleSuccess,
    paymentModal,
  };
}
